import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, inject, OnInit, Output, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AlertComponent, FormGroupComponent, ISelect, ModalComponent, SelectComponent } from '@tecalisdev/infinity';
import { IApikeyListBody } from '../../../settings/pages/apikeys/models/apikeys.models';
import { ApikeyService } from '../../../settings/pages/apikeys/services/apikey.service';
import { IDesignListBody } from '../../../settings/pages/design/models/design.models';
import { DesignService } from '../../../settings/pages/design/services/design.service';
import { ReactiveFormsService } from '../../../shared/services/reactive-forms.service';
import { MasterService } from '../../../shared/services/master.service';
import { SnackbarService } from '../../../shared/services/snackbar.service';
import { WorkflowsService } from '../../services/workflows.service';

@Component({
	selector: 'app-modal-workflow-duplicate',
	imports: [ReactiveFormsModule, TranslatePipe, ModalComponent, AlertComponent, FormGroupComponent, SelectComponent],
	templateUrl: './modal-workflow-duplicate.component.html',
	styleUrl: './modal-workflow-duplicate.component.scss',
})
export class ModalWorkflowDuplicateComponent implements OnInit {
	@Output() closeEvent = new EventEmitter<boolean>();

	private workflowsService = inject(WorkflowsService);
	private apikeyService = inject(ApikeyService);
	private designService = inject(DesignService);
	private snackbarService = inject(SnackbarService);
	private trans = inject(TranslateService);
	public masterService = inject(MasterService);
	public reactiveFormService = inject(ReactiveFormsService);

	public errorMsg: string = '';
	public defaultProfilesOptions: ISelect[] = [];
	public companiesOptions: ISelect[] = [];
	public apikeysOptions: ISelect[] = [];
	public themeOptions: ISelect[] = [];
	public readonly isSaving = signal(false);

	public form: FormGroup = new FormGroup({
		profile_uuids: new FormControl([], Validators.required),
		company: new FormControl(null, Validators.required),
		api_key_uuid: new FormControl({ value: null, disabled: true }, Validators.required),
		design_config_uuid: new FormControl({ value: null, disabled: true }, Validators.required),
	});

	/** Label del primario: «Crear» o «Guardando…» (tec-modal no expone loading). */
	public get primaryButtonLabel(): string {
		if (this.isSaving()) {
			return this.trans.instant('BUTTON.SAVING');
		}
		return this.trans.instant('BUTTON.SAVE');
	}

	ngOnInit(): void {
		this.defaultProfilesOptions = this.sortSelectOptions(this.masterService.defaultProfiles ?? []);
		this.companiesOptions = this.sortSelectOptions(this.masterService.companies ?? []);

		this.form.get('company')?.valueChanges.subscribe((company: ISelect | null) => {
			this.onCompanyChange(company);
		});
	}

	private sortSelectOptions(options: ISelect[]): ISelect[] {
		return [...options].sort((a, b) =>
			String(a.label ?? '').localeCompare(String(b.label ?? ''), undefined, { sensitivity: 'base' }),
		);
	}

	private onCompanyChange(company: ISelect | null): void {
		this.form.get('api_key_uuid')?.disable({ emitEvent: false });
		this.form.get('design_config_uuid')?.disable({ emitEvent: false });
		this.form.get('api_key_uuid')?.patchValue(null, { emitEvent: false });
		this.form.get('design_config_uuid')?.patchValue(null, { emitEvent: false });
		this.apikeysOptions = [];
		this.themeOptions = [];

		if (!company) return;

		const companyUuid = company?.id ?? company;

		this.apikeyService.list({ unpaginated: true, enabled: true, company_uuid: companyUuid }).subscribe({
			next: (response) => {
				this.apikeysOptions = this.sortSelectOptions(
					response.data.map((apikey: IApikeyListBody) => ({
						id: apikey.uuid,
						label: apikey.alias,
						value: apikey,
					})),
				);
				this.form.get('api_key_uuid')?.enable({ emitEvent: false });
			},
			error: () => {
				this.form.get('api_key_uuid')?.enable({ emitEvent: false });
			},
		});

		this.designService.list({ unpaginated: true, enabled: true, company_uuid: companyUuid }).subscribe({
			next: (response) => {
				this.themeOptions = this.sortSelectOptions(
					response.data.map((design: IDesignListBody) => ({
						id: design.uuid,
						label: design.title,
						value: design,
					})),
				);
				this.form.get('design_config_uuid')?.enable({ emitEvent: false });
			},
			error: () => {
				this.form.get('design_config_uuid')?.enable({ emitEvent: false });
			},
		});
	}

	public closeModal(type: string | null): void {
		if (type === 'buttonPrimary') {
			if (this.isSaving()) {
				return;
			}
			if (this.form.invalid) {
				this.form.markAllAsTouched();
				return;
			}
			this.submit();
			return;
		}

		if (this.isSaving()) {
			return;
		}
		this.closeEvent.emit(false);
	}

	private submit(): void {
		this.isSaving.set(true);
		this.errorMsg = '';
		const rawValue = this.form.getRawValue();

		const profileUuids: string[] = (rawValue.profile_uuids as ISelect[]).map((item: ISelect) =>
			String(item.id ? item.id : item),
		);
		const apiKeyUuid: string = rawValue.api_key_uuid?.id ?? rawValue.api_key_uuid;
		const designConfigUuid: string = rawValue.design_config_uuid?.id ?? rawValue.design_config_uuid;

		this.workflowsService
			.duplicate({
				profile_uuids: profileUuids,
				api_key_uuid: apiKeyUuid,
				design_config_uuid: designConfigUuid,
			})
			.subscribe({
				next: () => {
					this.snackbarService.show(this.trans.instant('WORKFLOWS.DUPLICATE.SUCCESS'));
					this.closeEvent.emit(true);
				},
				error: (error: HttpErrorResponse) => {
					this.errorMsg = this.trans.instant(
						error?.error?.error?.error_code === 1009
							? 'WORKFLOWS.FORM.ERROR_DESIGN_COMPANY'
							: 'WORKFLOWS.DUPLICATE.ERROR',
					);
					this.isSaving.set(false);
				},
				complete: () => {
					this.isSaving.set(false);
				},
			});
	}
}
